import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook for managing scroll position restoration in list views
 * Handles saving and restoring scroll position when navigating between list and detail pages
 */
const useScrollRestoration = (listType, items, isLoading) => {
    const location = useLocation();
    const hasRestoredRef = useRef(false);
    const scrollTimeoutRef = useRef(null);

    // Generate storage key based on list type
    const getStorageKey = useCallback(() => {
        return `scroll_position_${listType}`;
    }, [listType]);

    /**
     * Save scroll position and context before navigating to detail page
     * @param {string} itemId - ID of the item being clicked
     * @param {number} index - Index of the item in the current list
     * @param {object} filters - Current filter state
     */
    const saveScrollPosition = useCallback((itemId, index, filters = {}) => {
        const scrollData = {
            itemId,
            index,
            scrollY: window.scrollY,
            timestamp: Date.now(),
            filters: JSON.stringify(filters),
            // Save the next item ID as fallback
            nextItemId: items[index + 1]?.id || null,
        };

        try {
            sessionStorage.setItem(getStorageKey(), JSON.stringify(scrollData));
        } catch (error) {
            console.error('Failed to save scroll position:', error);
        }
    }, [getStorageKey, items]);

    /**
     * Get saved scroll position data
     * @returns {object|null} Saved scroll data or null
     */
    const getSavedScrollPosition = useCallback(() => {
        try {
            const data = sessionStorage.getItem(getStorageKey());
            return data ? JSON.parse(data) : null;
        } catch (error) {
            console.error('Failed to retrieve scroll position:', error);
            return null;
        }
    }, [getStorageKey]);

    /**
     * Clear saved scroll position
     */
    const clearScrollPosition = useCallback(() => {
        try {
            sessionStorage.removeItem(getStorageKey());
            hasRestoredRef.current = false;
        } catch (error) {
            console.error('Failed to clear scroll position:', error);
        }
    }, [getStorageKey]);

    /**
     * Find item index by ID in current list
     * @param {string} itemId - Item ID to find
     * @returns {number} Index of item or -1 if not found
     */
    const findItemIndex = useCallback((itemId) => {
        return items.findIndex(item => item.id === itemId);
    }, [items]);

    /**
     * Scroll to specific item by ID
     * @param {string} itemId - ID of item to scroll to
     * @param {string} behavior - Scroll behavior ('smooth' or 'auto')
     * @returns {boolean} True if scroll was successful
     */
    const scrollToItem = useCallback((itemId, behavior = 'auto') => {
        const element = document.getElementById(`${listType}-list-record-${itemId}`);
        if (element) {
            // Add a small delay to ensure the DOM is fully rendered
            scrollTimeoutRef.current = setTimeout(() => {
                element.scrollIntoView({ behavior, block: 'center' });
            }, 100);
            return true;
        }
        return false;
    }, [listType]);

    /**
     * Restore scroll position based on saved data
     */
    const restoreScrollPosition = useCallback(() => {
        // Only restore once per page load
        if (hasRestoredRef.current || isLoading || items.length === 0) {
            return;
        }

        const savedData = getSavedScrollPosition();
        if (!savedData) {
            return;
        }

        // Check if we're coming back from a detail page (has 'id' query param)
        const urlParams = new URLSearchParams(location.search);
        const returnFromDetailId = urlParams.get('id');

        if (!returnFromDetailId) {
            // Not returning from detail page, don't restore
            return;
        }

        // Verify the returned item matches what we saved
        if (returnFromDetailId !== savedData.itemId) {
            // Different item, don't restore (user might have navigated differently)
            clearScrollPosition();
            return;
        }

        hasRestoredRef.current = true;

        // Strategy 1: Try to scroll to the original item
        const itemIndex = findItemIndex(savedData.itemId);
        if (itemIndex !== -1) {
            scrollToItem(savedData.itemId);
            return;
        }

        // Strategy 2: Item not found, try to scroll to the next item (item B)
        if (savedData.nextItemId) {
            const nextItemIndex = findItemIndex(savedData.nextItemId);
            if (nextItemIndex !== -1) {
                scrollToItem(savedData.nextItemId);
                return;
            }
        }

        // Strategy 3: Fallback to approximate scroll position
        if (savedData.scrollY) {
            scrollTimeoutRef.current = setTimeout(() => {
                window.scrollTo({
                    top: savedData.scrollY,
                    behavior: 'auto'
                });
            }, 100);
        }
    }, [
        isLoading,
        items,
        getSavedScrollPosition,
        findItemIndex,
        scrollToItem,
        clearScrollPosition,
        location.search
    ]);

    /**
     * Effect to restore scroll position when items are loaded
     */
    useEffect(() => {
        if (!isLoading && items.length > 0) {
            restoreScrollPosition();
        }
    }, [isLoading, items.length, restoreScrollPosition]);

    /**
     * Cleanup on unmount
     */
    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    return {
        saveScrollPosition,
        restoreScrollPosition,
        clearScrollPosition,
        getSavedScrollPosition,
    };
};

export default useScrollRestoration;
